import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Brain, Eye, EyeSlash } from '@phosphor-icons/react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await register(email, password, name);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <Brain size={40} weight="duotone" className="text-[#007AFF]" />
            <span className="font-heading font-black text-2xl tracking-tight text-white uppercase">
              Trader Reset AI
            </span>
          </Link>
        </div>

        {/* Form Card */}
        <div className="bg-[#131313] border border-white/10 p-8" data-testid="register-form">
          <h1 className="font-heading font-bold text-2xl uppercase text-white mb-2 text-center">
            Join Trader Reset AI
          </h1>
          <p className="text-[#8892B0] text-center mb-8">
            Start your journey to disciplined trading
          </p>

          {error && (
            <div className="mb-6 p-4 bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] text-sm" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="name" className="text-xs text-[#8892B0] uppercase tracking-wider mb-2 block">
                Full Name
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-transparent border-white/20 text-white focus:border-[#007AFF] rounded-none p-4"
                placeholder="John Trader"
                required
                data-testid="register-name"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-xs text-[#8892B0] uppercase tracking-wider mb-2 block">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-transparent border-white/20 text-white focus:border-[#007AFF] rounded-none p-4"
                placeholder="trader@example.com"
                required
                data-testid="register-email"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-xs text-[#8892B0] uppercase tracking-wider mb-2 block">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-transparent border-white/20 text-white focus:border-[#007AFF] rounded-none p-4 pr-12"
                  placeholder="At least 6 characters"
                  required
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8892B0] hover:text-white"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-xs text-[#8892B0] uppercase tracking-wider mb-2 block">
                Confirm Password
              </Label>
              <Input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="bg-transparent border-white/20 text-white focus:border-[#007AFF] rounded-none p-4"
                placeholder="Repeat your password"
                required
                data-testid="register-confirm-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-[#007AFF] text-white font-bold uppercase tracking-widest hover:bg-[#005bb5] transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              data-testid="register-submit"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>

          <div className="flex items-center justify-center gap-2 mt-6">
            <img src={process.env.REACT_APP_LOGO_URL} alt="Logo" className="w-6 h-6 flex-shrink-0" />
            <p className="text-[#8892B0] whitespace-nowrap">
              Already have an account?{' '}
              <Link to="/login" className="text-[#007AFF] hover:underline" data-testid="register-login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
